import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  addCardMemberBodySchema,
  cardCompletionPatchSchema,
  cardMirrorSchema,
  cardCoverPutSchema,
  cardDescriptionPatchSchema,
  cardFieldVersionsSchema,
  linkCardSchema,
  cardDetailSchema,
  cardDueDatePatchSchema,
  cardDueReminderPatchSchema,
  cardMovePreviewQuerySchema,
  cardMoveToBoardBodySchema,
  cardMoveToBoardResultSchema,
  cardRestoreBodySchema,
  cardStartDatePatchSchema,
  reconciliationSchema,
  cardSchema,
  type BoardContents,
  type CardMirror,
  type BoardFaceList,
  type Card,
  type CardCompletionPatch,
  type CardCustomFieldBadge,
  type CardCoverPut,
  type CardDescriptionPatch,
  type CardDetail,
  type CardDueDatePatch,
  type CardDueReminderPatch,
  type CardFieldVersions,
  type CardLocationPut,
  type CardMoveBody,
  type CardMovePreviewQuery,
  type CardMoveToBoardBody,
  type CardMoveToBoardResult,
  type CardRestoreBody,
  type CardStartDatePatch,
  type CardTitlePatch,
  type CreateCardBody,
  type LinkCard,
  type AddCardMemberBody,
  type Reconciliation,
} from '@molt/shared';
import { z } from 'zod';
import { announce } from './announce';
import { del, emptyResponseSchema, get, patch, post, put } from './api';
import { cardAgingStageForMode } from './card-aging';
import {
  boardMoveInvalidationKeys,
  removeCardFromBoardContents,
  setBoardContentsQueries,
} from './cross-board-move';
import { useInterfaceLanguage } from './interface-language';
import { optimisticMutation } from './optimistic';
import { queryKeys } from './query-keys';

/**
 * The card mutations — chunk `C3`, agent A (`FE-12.1`, `FE-12.2`, `FE-12.3`).
 *
 * `lib/lists.ts` is the template for every part of this file — endpoint
 * functions, then pure updaters, then hooks — and this file follows its shape
 * on purpose rather than inventing a second one.
 *
 * THE RULE THIS FILE HOLDS (brief §3.1, same as `lists.ts`): the client never
 * computes a rank. `POST /cards` and `POST /cards/:id/move` take the two cards
 * FLANKING the gap the card is created or dropped into, and there is no
 * `position` key on any request body in this product. Nothing here imports
 * `rankBetween`, `ranksBetween`, `firstRank` or `compareRanks` from
 * `@molt/shared` — apps/web must stay at zero references to all four.
 *
 * The corollary (brief §5.3): an optimistic update cannot produce a valid
 * `position`, so a move is a SPLICE OF THE ARRAYS and every card's `position`
 * field is left exactly as it was. Nothing in `apps/web` sorts by `position` —
 * `BoardFace` and `ListColumn` render `list.cards` in payload order — so the
 * array is the render order and the stale rank is unused until the refetch
 * lands.
 *
 * ⚠ **§3.2's asymmetry, restated because it is the likeliest silent bug in
 * this file**: `{ previousCardId: null, nextCardId: null }` is LEGAL on both
 * `POST /cards` and `POST /cards/:id/move`, and means "the bottom of the
 * target list" on both — including an EMPTY list, which has no neighbours to
 * name. This is NOT how `PATCH /lists/:id/position` reads the same pair
 * (`lists.ts`'s `applyOptimisticMove` never actually receives it, because
 * `listPositionPatchSchema` refuses it with a 422). Both card updaters below
 * — create and move alike — read the pair identically via one shared
 * `insertAtNeighbours` helper, on purpose: there is exactly one reading of
 * "where do these two ids put the card" in this file, not two that happen to
 * agree today.
 *
 * `announceRollback` and `trimmedOrThrow` below are COPIES of the
 * module-private helpers of the same name in `lib/lists.ts`, not imports —
 * that file is not this agent's to change, and a two-file edit to save eight
 * lines is not worth a cross-agent collision (brief §6.1). If a later chunk
 * wants one copy, it can extract both into a shared module; this one
 * deliberately keeps the duplication rather than reaching into `lists.ts`.
 *
 * This file spells its own paths, exactly as `lists.ts`'s header explains:
 * `lib/api.ts` forbids adding route strings to its `api` object, so a block's
 * endpoint function is one line on top of one of its verbs, in the block's
 * own file.
 *
 * ⏭ FORWARD — `restoreCard`, `deleteCard` and the archived-items panel are
 * chunk `F3`'s (`FE-22.2`, `FE-22.3`), exactly as `lib/lists.ts` records for
 * lists (`C2-BRIEF.md` §2.1, carried into `C3-BRIEF.md` §2.1 item 3).
 * `FR-EXP-010`'s *Archived items* panel owns browse-and-restore for both
 * lists and cards, and lives in the board menu `FE-08.2` owns. The route
 * `POST /cards/:id/restore` exists; this chunk does not call it. F3 adds its
 * endpoint functions HERE, on `lists.ts`'s own precedent, rather than opening
 * a second cards module.
 */

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

const cardCreateOrMirrorResultSchema = z.union([
  cardMirrorSchema,
  linkCardSchema,
  cardSchema,
]);

export type CardCreateOrMirrorResult = Card | LinkCard | CardMirror;

export function createCard(body: CreateCardBody): Promise<CardCreateOrMirrorResult> {
  return post('/cards', cardCreateOrMirrorResultSchema, body);
}

export interface CardFieldVersionHeader {
  readonly fieldVersion?: string | undefined;
}

function fieldVersionInit(
  options: CardFieldVersionHeader | undefined,
): Omit<RequestInit, 'method' | 'body'> | undefined {
  return options?.fieldVersion === undefined
    ? undefined
    : { headers: { 'if-field-version': options.fieldVersion } };
}

export function renameCard(
  cardId: string,
  body: CardTitlePatch,
  options?: CardFieldVersionHeader,
): Promise<Card> {
  return patch(
    `/cards/${encodeURIComponent(cardId)}/title`,
    cardSchema,
    body,
    fieldVersionInit(options),
  );
}

export function moveCard(cardId: string, body: CardMoveBody): Promise<Card> {
  return post(`/cards/${encodeURIComponent(cardId)}/move`, cardSchema, body);
}

export function moveCardToBoard(
  cardId: string,
  body: CardMoveToBoardBody,
): Promise<CardMoveToBoardResult> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/move-to-board`,
    cardMoveToBoardResultSchema,
    cardMoveToBoardBodySchema.parse(body),
  );
}

export function previewCardMoveToBoard(
  cardId: string,
  query: CardMovePreviewQuery,
): Promise<Reconciliation> {
  const params = cardMovePreviewQuerySchema.parse(query);
  const search = new URLSearchParams({
    boardId: params.boardId,
    listId: params.listId,
  });
  return get(
    `/cards/${encodeURIComponent(cardId)}/move-preview?${search.toString()}`,
    reconciliationSchema,
  );
}

/**
 * ⚠ §3.3 / brief `C3-BRIEF.md` §3: `POST /cards/:id/archive` takes NO BODY.
 * Called as `post(path, schema)` with no third argument, exactly as
 * `archiveList` in `lists.ts` calls its own archive route — passing `{}`
 * instead sets `content-type: application/json` with an empty body, and
 * Fastify answers `FST_ERR_CTP_EMPTY_JSON_BODY`. This is the precise defect
 * C2 shipped and fixed in `lib/api.ts`: the archive silently did nothing,
 * because the optimistic write removed the card, the request 400'd, and the
 * rollback put it back before anyone could see the flash.
 */
export function archiveCard(cardId: string): Promise<Card> {
  return post(`/cards/${encodeURIComponent(cardId)}/archive`, cardSchema);
}

export function archiveCardDetail(cardId: string): Promise<CardDetail> {
  return post(`/cards/${encodeURIComponent(cardId)}/archive`, cardDetailSchema);
}

export function restoreCard(cardId: string, body?: CardRestoreBody): Promise<CardDetail> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/restore`,
    cardDetailSchema,
    body === undefined ? undefined : cardRestoreBodySchema.parse(body),
  );
}

export function deleteCard(cardId: string): Promise<null> {
  return del(`/cards/${encodeURIComponent(cardId)}`, emptyResponseSchema);
}

export function getCardDetail(cardId: string): Promise<CardDetail> {
  return get(`/cards/${encodeURIComponent(cardId)}`, cardDetailSchema);
}

export function getCardFieldVersions(cardId: string): Promise<CardFieldVersions> {
  return get(
    `/cards/${encodeURIComponent(cardId)}/field-versions`,
    cardFieldVersionsSchema,
  );
}

export function patchCardDescription(
  cardId: string,
  body: CardDescriptionPatch,
  options?: CardFieldVersionHeader,
): Promise<CardDetail> {
  return patch(
    `/cards/${encodeURIComponent(cardId)}/description`,
    cardDetailSchema,
    cardDescriptionPatchSchema.parse(body),
    fieldVersionInit(options),
  );
}

export function patchCardStartDate(
  cardId: string,
  body: CardStartDatePatch,
): Promise<CardDetail> {
  return patch(
    `/cards/${encodeURIComponent(cardId)}/start-date`,
    cardDetailSchema,
    cardStartDatePatchSchema.parse(body),
  );
}

export function patchCardDueDate(
  cardId: string,
  body: CardDueDatePatch,
): Promise<CardDetail> {
  return patch(
    `/cards/${encodeURIComponent(cardId)}/due-date`,
    cardDetailSchema,
    cardDueDatePatchSchema.parse(body),
  );
}

export function patchCardDueReminder(
  cardId: string,
  body: CardDueReminderPatch,
): Promise<CardDetail> {
  return patch(
    `/cards/${encodeURIComponent(cardId)}/due-date-reminder`,
    cardDetailSchema,
    cardDueReminderPatchSchema.parse(body),
  );
}

export function patchCardCompleted(
  cardId: string,
  body: CardCompletionPatch,
): Promise<CardDetail> {
  return patch(
    `/cards/${encodeURIComponent(cardId)}/completed`,
    cardDetailSchema,
    cardCompletionPatchSchema.parse(body),
  );
}

export function putCardCover(cardId: string, body: CardCoverPut): Promise<CardDetail> {
  return put(
    `/cards/${encodeURIComponent(cardId)}/cover`,
    cardDetailSchema,
    cardCoverPutSchema.parse(body),
  );
}

export function deleteCardCover(cardId: string): Promise<CardDetail> {
  return del(`/cards/${encodeURIComponent(cardId)}/cover`, cardDetailSchema);
}

export function addCardMember(
  cardId: string,
  body: AddCardMemberBody,
): Promise<CardDetail> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/members`,
    cardDetailSchema,
    addCardMemberBodySchema.parse(body),
  );
}

export function removeCardMember(cardId: string, userId: string): Promise<CardDetail> {
  return del(
    `/cards/${encodeURIComponent(cardId)}/members/${encodeURIComponent(userId)}`,
    cardDetailSchema,
  );
}

export function cardDetailQueryOptions(cardId: string) {
  return queryOptions({
    queryKey: queryKeys.cards.detail(cardId),
    queryFn: () => getCardDetail(cardId),
  });
}

export function cardFieldVersionsQueryOptions(cardId: string) {
  return queryOptions({
    queryKey: queryKeys.cards.fieldVersions(cardId),
    queryFn: () => getCardFieldVersions(cardId),
  });
}

// ---------------------------------------------------------------------------
// Variables
// ---------------------------------------------------------------------------

export interface CreateCardVariables {
  readonly listId: string;
  readonly title: string;
  /** The card immediately ABOVE the gap; null with `nextCardId` null means the bottom of the list. */
  readonly previousCardId: string | null;
  /** The card immediately BELOW the gap; alone, it prepends (`FR-BOARD-020` AC2). */
  readonly nextCardId: string | null;
  readonly location?: CardLocationPut | undefined;
}

export interface RenameCardVariables {
  readonly cardId: string;
  /** Client-side only, never on the wire — the updater needs it to find the card without scanning every list. */
  readonly listId: string;
  readonly title: string;
  readonly fieldVersion?: string | undefined;
}

export interface MoveCardVariables {
  readonly cardId: string;
  /** Client-side only — which list the card is leaving, so the updater can splice both arrays. */
  readonly fromListId: string;
  /** The DESTINATION list. This one IS on the wire, as `listId`. */
  readonly toListId: string;
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
}

export interface MoveCardToBoardVariables {
  readonly cardId: string;
  readonly title: string;
  readonly sourceBoardId: string;
  readonly sourceListId: string;
  readonly destinationBoardId: string;
  readonly destinationListId: string;
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
}

export interface ArchiveCardVariables {
  readonly cardId: string;
  /** Client-side only — see `RenameCardVariables`. */
  readonly listId: string;
  /** Client-side only — §5.5's rollback sentence names the card. */
  readonly title: string;
}

export interface RestoreCardVariables {
  readonly cardId: string;
  readonly listId?: string | undefined;
}

export interface DeleteCardVariables {
  readonly cardId: string;
  readonly title: string;
}

export interface UpdateCardDescriptionVariables {
  readonly cardId: string;
  readonly boardId: string;
  readonly description: string;
  readonly fieldVersion?: string | undefined;
}

export interface UpdateCardStartDateVariables {
  readonly cardId: string;
  readonly boardId: string;
  readonly startAt: string | null;
}

export interface UpdateCardDueDateVariables {
  readonly cardId: string;
  readonly boardId: string;
  readonly dueAt: string | null;
  readonly dueAllDay?: boolean;
}

export interface UpdateCardDueReminderVariables {
  readonly cardId: string;
  readonly boardId: string;
  readonly dueReminderMinutesBefore: CardDueReminderPatch['dueReminderMinutesBefore'];
}

export interface SetCardCompletedVariables {
  readonly cardId: string;
  readonly boardId: string;
  readonly completed: boolean;
}

export interface SetCardCoverVariables {
  readonly cardId: string;
  readonly boardId: string;
  readonly cover: CardCoverPut;
}

export interface RemoveCardCoverVariables {
  readonly cardId: string;
  readonly boardId: string;
}

export interface ToggleCardMemberVariables {
  readonly cardId: string;
  readonly boardId: string;
  readonly userId: string;
}

// ---------------------------------------------------------------------------
// Placeholder ids
// ---------------------------------------------------------------------------

const OPTIMISTIC_CARD_ID_PREFIX = 'optimistic:';

/**
 * The id an optimistically created card carries until the server answers.
 *
 * Deliberately NOT a ULID — same argument as `optimisticListId` in
 * `lists.ts`: a placeholder that looks real is a placeholder that reaches a
 * route as a neighbour id and 404s for reasons nobody can trace. The
 * `optimistic:` prefix is greppable, and `isOptimisticCardId` is the gate
 * brief §5.4 names for the four sites that must not treat a placeholder as a
 * real card: `CardTile`, `SortableCardTile`, `QuickEdit`, and
 * `card-drag`'s neighbour resolution.
 */
export function optimisticCardId(): string {
  return `${OPTIMISTIC_CARD_ID_PREFIX}${crypto.randomUUID()}`;
}

export function isOptimisticCardId(id: string): boolean {
  return id.startsWith(OPTIMISTIC_CARD_ID_PREFIX);
}

// ---------------------------------------------------------------------------
// Optimistic updaters — pure, so they are testable without React
// ---------------------------------------------------------------------------

/**
 * ⚠ Every updater must be PURE AND IMMUTABLE: copy the array, the list, and
 * the card you change. Mutating in place defeats `lib/optimistic.ts`'s
 * snapshot, and the rollback then restores the object you already mutated —
 * which looks exactly like the rollback silently not working.
 */

/**
 * Builds the placeholder `BoardFaceList['cards'][number]` a create inserts.
 *
 * `position`, `shortLink` and `path` are the empty string, `cardNumber` is
 * `0` — all four are server-generated (brief §5.3) and a card nobody has
 * told the server about yet cannot have a real one. Every Phase 2 / badge
 * field (`labels`, `members`, aging, dates, checklist and attachment counts,
 * `cover`, `voteCount`, `customFieldBadges`) takes its zero value: a card
 * this new cannot yet carry any of what those fields describe.
 *
 * ⚠ **A placeholder card cannot render as `<CardTile as="a" href={card.path}>`
 * — `path: ''` is an anchor to nowhere.** §6.4 is agent E's fix (an
 * optimistic card renders as a `div` with `aria-busy`, gated on
 * `isOptimisticCardId`); this function only has to make the empty string
 * available for that gate to check, not avoid producing it.
 */
function placeholderCard(
  title: string,
  placeholderId: string,
  boardId: string,
  listId: string,
): BoardFaceList['cards'][number] {
  return {
    approvals: null,
    id: placeholderId,
    boardId,
    listId,
    title,
    position: '',
    shortLink: '',
    cardNumber: 0,
    ref: null,
    path: '',
    archived: false,
    createdAt: '',
    updatedAt: '',
    agingExempt: false,
    lastActivityAt: '',
    agingStage: null,
    labels: [],
    startAt: null,
    dueAt: null,
    dueAllDay: false,
    completed: false,
    hasDescription: false,
    commentCount: 0,
    members: [],
    cover: null,
    checklistItemCount: 0,
    checklistCheckedItemCount: 0,
    attachmentCount: 0,
    customFieldBadges: [],
    /**
     * `[]` — a card nobody has told the server about yet cannot carry a
     * sticker, and `FR-CARD-032` places one on a card that already exists.
     * The ordinary zero value, on `labels`' and `members`' footing above.
     */
    stickers: [],
    /**
     * `false`, NOT `null` — and the two are different claims.
     * `boardFaceCardSchema.watching` uses `null` for "there is no *you*",
     * which is an anonymous reader; a card create always has an actor, so
     * `null` here would be a lie of a kind `voteCount`'s `null` below is not.
     *
     * ⚠ `false` is also the truthful optimistic value rather than a guess:
     * auto-watch (`apps/server/src/notifications/auto-watch.ts`) is derived
     * from `member.added` and the comment events, and **not** from a card
     * create — so the server's own answer for a card the actor has just made
     * and not yet joined is `false` too, and the placeholder does not flicker
     * when the real card replaces it.
     */
    watching: false,
    // `null`, not `0` — every real card the server sends today carries
    // `voteCount: null` (`apps/server/src/routes/cards.ts`,
    // `board-contents.ts`: votes are unbuilt, B59, and `null` is what an
    // unmodelled source answers, on `boardFaceCardSchema`'s own "badges
    // whose sources do not exist yet are omitted" footing — `voteCount` was
    // never converted to the zeroed convention `checklistItemCount` and
    // `attachmentCount` were). `0` would make the placeholder the one card
    // on the board with a real number in this field, which is a real
    // divergence from server truth even though nothing renders it yet
    // (`CardTile`'s own header: `voteCount` is Phase 2, unrendered today).
    voteCount: null,
  };
}

/**
 * Inserts `card` into `cards` at the gap `previousCardId`/`nextCardId`
 * describe, reading the pair the way BOTH card routes read it (brief §3.2):
 *
 *   - `nextCardId` given (with or without `previousCardId`): insert
 *     immediately before it. `previousCardId`, when also given, only
 *     confirms which gap — the array's own order decides the insertion
 *     point, exactly as `lists.ts`'s `applyOptimisticCreate` reasons about
 *     "both given" for lists.
 *   - `previousCardId` alone: insert immediately after it.
 *   - both `null`: append at the end — the BOTTOM of the list, which is
 *     legal here (unlike a list's own `PATCH /lists/:id/position`) and is
 *     how a card lands in an EMPTY list.
 *
 * A named neighbour missing from `cards` falls back to appending at the end,
 * on `lists.ts`'s own precedent — this is reachable only if the cache is
 * already stale relative to the id it was given, and appending is a safe,
 * visible-and-correctable landing rather than a thrown error.
 *
 * Shared by `applyOptimisticCardCreate` and `applyOptimisticCardMove`
 * because creating at a gap and dropping at a gap are the same placement
 * question — one function, one reading of the pair, never two that happen to
 * agree today.
 */
function insertAtNeighbours<T extends { id: string }>(
  cards: readonly T[],
  card: T,
  previousCardId: string | null,
  nextCardId: string | null,
): T[] {
  if (nextCardId !== null) {
    const index = cards.findIndex((c) => c.id === nextCardId);
    if (index === -1) return [...cards, card];
    return [...cards.slice(0, index), card, ...cards.slice(index)];
  }
  if (previousCardId !== null) {
    const index = cards.findIndex((c) => c.id === previousCardId);
    if (index === -1) return [...cards, card];
    return [...cards.slice(0, index + 1), card, ...cards.slice(index + 1)];
  }
  return [...cards, card];
}

/**
 * Inserts the placeholder into `variables.listId`'s card array and
 * increments both `cardCount` and `openCardCount` on that list (brief §5.2):
 * a newly created card is open and not archived, so it belongs in both
 * counts, and `ListColumn`'s header renders `openCardCount` — never
 * `cards.length` — so missing this increment is a badge that disagrees with
 * the cards under it until the refetch lands.
 *
 * Trims `variables.title` and no-ops (returns `previous` unchanged) when the
 * trimmed result is empty, and no-ops when `variables.listId` names no list
 * in the cache — the same "no-op, not an error" footing `lists.ts` takes for
 * an id the cache does not know about. The 422 is the backstop, never the
 * mechanism (brief §3.3).
 */
export function applyOptimisticCardCreate(
  previous: BoardContents | undefined,
  variables: CreateCardVariables,
  placeholderId: string,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  const title = variables.title.trim();
  if (title.length === 0) return previous;

  const listIndex = previous.lists.findIndex((list) => list.id === variables.listId);
  if (listIndex === -1) return previous;

  const targetList = previous.lists[listIndex]!;
  const placeholder = placeholderCard(
    title,
    placeholderId,
    previous.board.id,
    variables.listId,
  );
  const cards = insertAtNeighbours(
    targetList.cards,
    placeholder,
    variables.previousCardId,
    variables.nextCardId,
  );

  return {
    ...previous,
    lists: previous.lists.map((list, index) =>
      index === listIndex
        ? {
            ...list,
            cards,
            cardCount: list.cardCount + 1,
            openCardCount: list.openCardCount + 1,
          }
        : list,
    ),
  };
}

/**
 * `FR-BOARD-022` AC2: renaming a card does not move it — changes `title`
 * alone, on `applyOptimisticRename`'s footing in `lists.ts`.
 *
 * Trims and no-ops on an empty result, matching `FR-BOARD-022` AC3: "Escape
 * closes without saving" and the 422's own message, "the previous title
 * stands".
 */
export function applyOptimisticCardRename(
  previous: BoardContents | undefined,
  variables: RenameCardVariables,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  const title = variables.title.trim();
  if (title.length === 0) return previous;

  const listIndex = previous.lists.findIndex((list) => list.id === variables.listId);
  if (listIndex === -1) return previous;

  const targetList = previous.lists[listIndex]!;
  if (!targetList.cards.some((card) => card.id === variables.cardId)) return previous;

  return {
    ...previous,
    lists: previous.lists.map((list, index) =>
      index === listIndex
        ? {
            ...list,
            cards: list.cards.map((card) =>
              card.id === variables.cardId ? { ...card, title } : card,
            ),
          }
        : list,
    ),
  };
}

/**
 * Splices the card out of `fromListId`'s array and into `toListId`'s at the
 * gap `previousCardId`/`nextCardId` describe (via `insertAtNeighbours`,
 * §3.2's reading). Leaves the card's `position` field untouched — brief
 * §5.3.
 *
 * ⚠ DO NOT "FIX" THIS TO WRITE A NEW `position`. The server is the sole rank
 * authority (brief §3.1): a client-computed rank is not merely redundant, it
 * would be a value nothing minted with `rankBetween` and could collide with
 * a concurrent drag. The array is stale-but-correct until the refetch lands
 * — `position` is stale-and-unused, because nothing in `apps/web` sorts by
 * it.
 *
 * A CROSS-LIST move (`fromListId !== toListId`) also updates the moved
 * card's own `listId` field to the destination, and decrements the source
 * list's `cardCount`/`openCardCount` while incrementing the destination's
 * (brief §5.2) — the card is leaving one list's population and entering
 * another's, open in both. A WITHIN-list reorder touches neither count: the
 * card never leaves the list it is already counted in, only its position in
 * the rendered array changes.
 *
 * No-ops (returns `previous` unchanged) when `fromListId`, `toListId` or the
 * card itself is not found in the cache — a stale drag target is a safe
 * no-op here, not a thrown error; the mutation's own request is still what
 * carries the true state to the server.
 */
export function applyOptimisticCardMove(
  previous: BoardContents | undefined,
  variables: MoveCardVariables,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  const fromListIndex = previous.lists.findIndex(
    (list) => list.id === variables.fromListId,
  );
  const toListIndex = previous.lists.findIndex((list) => list.id === variables.toListId);
  if (fromListIndex === -1 || toListIndex === -1) return previous;

  const fromList = previous.lists[fromListIndex]!;
  const cardIndex = fromList.cards.findIndex((card) => card.id === variables.cardId);
  if (cardIndex === -1) return previous;

  const moving = fromList.cards[cardIndex]!;
  const crossList = variables.fromListId !== variables.toListId;
  const movedCard = crossList ? { ...moving, listId: variables.toListId } : moving;

  const withoutMoving = [
    ...fromList.cards.slice(0, cardIndex),
    ...fromList.cards.slice(cardIndex + 1),
  ];

  if (!crossList) {
    const reordered = insertAtNeighbours(
      withoutMoving,
      movedCard,
      variables.previousCardId,
      variables.nextCardId,
    );
    return {
      ...previous,
      lists: previous.lists.map((list, index) =>
        index === fromListIndex ? { ...list, cards: reordered } : list,
      ),
    };
  }

  const toList = previous.lists[toListIndex]!;
  const inserted = insertAtNeighbours(
    toList.cards,
    movedCard,
    variables.previousCardId,
    variables.nextCardId,
  );

  return {
    ...previous,
    lists: previous.lists.map((list, index) => {
      if (index === fromListIndex) {
        return {
          ...list,
          cards: withoutMoving,
          cardCount: list.cardCount - 1,
          openCardCount: list.openCardCount - 1,
        };
      }
      if (index === toListIndex) {
        return {
          ...list,
          cards: inserted,
          cardCount: list.cardCount + 1,
          openCardCount: list.openCardCount + 1,
        };
      }
      return list;
    }),
  };
}

export function applyOptimisticCardMoveToBoard(
  previous: BoardContents | undefined,
  variables: MoveCardToBoardVariables,
): BoardContents | undefined {
  return removeCardFromBoardContents(previous, variables.cardId);
}

/**
 * Removes the card from its list's `cards` array entirely — the same
 * "gone, not relabelled" footing `applyOptimisticArchive` takes for a list in
 * `lists.ts`, and consistent with `boardFaceListSchema.cards`'s own
 * documented behaviour: an archived card is absent from the board face, not
 * present with `archived: true`.
 *
 * Decrements `openCardCount` alone (brief §5.2) — `cardCount` counts a
 * list's cards "open and archived alike" (`boardFaceListSchema`'s own
 * docstring), and archiving does not delete the row, so `cardCount` is
 * unchanged. This is the one updater in this file where the two counts are
 * meant to diverge, and that divergence is `FR-BOARD-016`'s delete
 * confirmation's whole point on the list side — see `list.ts` for the fuller
 * argument, which transfers unchanged.
 */
export function applyOptimisticCardArchive(
  previous: BoardContents | undefined,
  variables: ArchiveCardVariables,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  const listIndex = previous.lists.findIndex((list) => list.id === variables.listId);
  if (listIndex === -1) return previous;

  const targetList = previous.lists[listIndex]!;
  if (!targetList.cards.some((card) => card.id === variables.cardId)) return previous;

  return {
    ...previous,
    lists: previous.lists.map((list, index) =>
      index === listIndex
        ? {
            ...list,
            cards: list.cards.filter((card) => card.id !== variables.cardId),
            openCardCount: list.openCardCount - 1,
          }
        : list,
    ),
  };
}

export function applyCardDetailToBoardContents(
  previous: BoardContents | undefined,
  detail: CardDetail,
  now: string | number | Date = new Date(),
): BoardContents | undefined {
  if (previous === undefined) return undefined;
  const customFieldBadges = customFieldBadgesFromDetail(detail);

  return {
    ...previous,
    lists: previous.lists.map((list) => ({
      ...list,
      cards: list.cards.map((card) =>
        cardMatchesDetail(card, detail)
          ? {
              ...card,
              title: detail.title,
              listId: card.id === detail.id ? detail.listId : card.listId,
              agingExempt: detail.agingExempt,
              lastActivityAt: detail.lastActivityAt,
              agingStage: cardAgingStageForMode(
                previous.board.cardAgingMode,
                detail,
                now,
              ),
              hasDescription: detail.description !== null,
              startAt: detail.startAt,
              dueAt: detail.dueAt,
              dueAllDay: detail.dueAllDay,
              completed: detail.completed,
              labels: detail.labels,
              members: detail.members,
              cover: detail.cover,
              customFieldBadges,
              checklistItemCount: detail.checklists.reduce(
                (total, checklist) => total + checklist.items.length,
                0,
              ),
              checklistCheckedItemCount: detail.checklists.reduce(
                (total, checklist) =>
                  total + checklist.items.filter((item) => item.checked).length,
                0,
              ),
              attachmentCount: detail.attachments.length,
            }
          : card,
      ),
    })),
  };
}

function cardMatchesDetail(
  card: BoardFaceList['cards'][number],
  detail: CardDetail,
): boolean {
  if (card.id === detail.id) return true;
  return (
    'surfaceType' in card &&
    card.surfaceType === 'mirror' &&
    card.sourceCardId === detail.id
  );
}

function customFieldBadgesFromDetail(detail: CardDetail): CardCustomFieldBadge[] {
  return detail.customFields.flatMap((field) =>
    !field.hidden && field.showOnFront && field.value !== null
      ? [
          {
            definitionId: field.id,
            name: field.name,
            type: field.type,
            value: field.value,
          },
        ]
      : [],
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Trims and refuses an empty result — the client-side backstop for
 * `FR-BOARD-020`'s and `FR-BOARD-022`'s "whitespace-only is refused" (the
 * 422 is the mechanism's last line, not its first).
 *
 * COPIED from `lib/lists.ts`'s module-private function of the same name,
 * not imported — see this file's header for why.
 */
function trimmedOrThrow(title: string): string {
  const trimmed = title.trim();
  if (trimmed.length === 0) {
    throw new Error('A card title cannot be empty.');
  }
  return trimmed;
}

/**
 * Roll back, then say so — the one home for failure announcements in this
 * file (brief §5.5).
 *
 * COPIED from `lib/lists.ts`'s module-private function of the same name, not
 * imported — see this file's header for why. The reasoning transfers
 * unchanged: C2 shipped a create that announced success on a failed request
 * because three agents put the rollback announcement in three different
 * places, one of which was no place at all. A call site can forget this. A
 * hook cannot: every caller of a `use*Card` hook gets the rollback
 * announcement whether or not it thought about failure.
 *
 * `optimisticMutation`'s own `onError` still does the restoring — this only
 * wraps it, and must always call it first. A silent snap-back is
 * indistinguishable from a bug, which is `announce.ts`'s point: one channel,
 * and no `Notice`, because none of these is an error the member caused.
 */
function announceRollback<TVariables, TContext>(
  handlers: {
    onError: (error: unknown, variables: TVariables, context: TContext) => void;
  },
  message: (variables: TVariables) => string,
): (error: unknown, variables: TVariables, context: TContext) => void {
  return (error, variables, context) => {
    handlers.onError(error, variables, context);
    announce(message(variables));
  };
}

export function useCreateCard(
  boardId: string,
): UseMutationResult<CardCreateOrMirrorResult, unknown, CreateCardVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  const key = queryKeys.boards.contents(boardId);

  const handlers = optimisticMutation<CreateCardVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: (previous, variables) =>
      applyOptimisticCardCreate(previous, variables, optimisticCardId()),
  });

  return useMutation({
    mutationFn: (variables: CreateCardVariables) =>
      createCard({
        listId: variables.listId,
        title: trimmedOrThrow(variables.title),
        previousCardId: variables.previousCardId,
        nextCardId: variables.nextCardId,
        ...(variables.location === undefined ? {} : { location: variables.location }),
      }),
    ...handlers,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(boardId),
      });
    },
    onError: announceRollback(handlers, (variables) =>
      t('primitives.cardHooks.createFailed', { title: variables.title }),
    ),
  });
}

export function useRenameCard(
  boardId: string,
): UseMutationResult<Card, unknown, RenameCardVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  const key = queryKeys.boards.contents(boardId);

  const handlers = optimisticMutation<RenameCardVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: applyOptimisticCardRename,
  });

  return useMutation({
    mutationFn: (variables: RenameCardVariables) =>
      renameCard(
        variables.cardId,
        { title: trimmedOrThrow(variables.title) },
        { fieldVersion: variables.fieldVersion },
      ),
    ...handlers,
    onError: announceRollback(handlers, () => t('primitives.cardHooks.renameFailed')),
  });
}

export function useMoveCard(
  boardId: string,
): UseMutationResult<Card, unknown, MoveCardVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  const key = queryKeys.boards.contents(boardId);

  const handlers = optimisticMutation<MoveCardVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: applyOptimisticCardMove,
  });

  return useMutation({
    mutationFn: (variables: MoveCardVariables) =>
      moveCard(variables.cardId, {
        listId: variables.toListId,
        previousCardId: variables.previousCardId,
        nextCardId: variables.nextCardId,
      }),
    ...handlers,
    // ⚠ Brief §5.3/§3.3: a 409 or 404 from the move route is a normal
    // outcome — the board closed, neighbours out of order, an archived
    // destination list — not a crash. No red notice; `announce()` is the
    // only channel, per `announce.ts`'s one-event-one-channel rule.
    onError: announceRollback(handlers, () => t('primitives.cardHooks.moveFailed')),
  });
}

export function useMoveCardToBoard(
  sourceBoardId: string,
): UseMutationResult<CardMoveToBoardResult, unknown, MoveCardToBoardVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables: MoveCardToBoardVariables) =>
      moveCardToBoard(variables.cardId, {
        listId: variables.destinationListId,
        previousCardId: variables.previousCardId,
        nextCardId: variables.nextCardId,
      }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.boards.contents(sourceBoardId),
      });
      const previous = queryClient.getQueriesData<BoardContents>({
        queryKey: queryKeys.boards.contents(sourceBoardId),
      });
      setBoardContentsQueries(queryClient, sourceBoardId, (current) =>
        removeCardFromBoardContents(current, variables.cardId),
      );
      return { previous };
    },
    onError: (_error, variables, context) => {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
      announce(t('primitives.cardHooks.moveToBoardFailed', { title: variables.title }));
    },
    onSuccess: (result, variables) => {
      queryClient.setQueryData(queryKeys.cards.detail(variables.cardId), result.card);
      for (const key of boardMoveInvalidationKeys(
        sourceBoardId,
        variables.destinationBoardId,
      )) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(sourceBoardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(variables.destinationBoardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(variables.cardId),
      });
    },
  });
}

export function useArchiveCard(
  boardId: string,
): UseMutationResult<Card, unknown, ArchiveCardVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  const key = queryKeys.boards.contents(boardId);

  const handlers = optimisticMutation<ArchiveCardVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: applyOptimisticCardArchive,
  });

  return useMutation({
    mutationFn: (variables: ArchiveCardVariables) => archiveCard(variables.cardId),
    ...handlers,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(boardId),
      });
    },
    onError: announceRollback(handlers, () => t('primitives.cardHooks.archiveFailed')),
  });
}

export function useArchiveCardDetail(
  boardId: string,
): UseMutationResult<CardDetail, unknown, ArchiveCardVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables: ArchiveCardVariables) => archiveCardDetail(variables.cardId),
    onSuccess: (detail, variables) => {
      queryClient.setQueryData(queryKeys.cards.detail(variables.cardId), detail);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.archiveAll(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(boardId),
      });
    },
    onError: () => {
      announce(t('primitives.cardHooks.archiveFailed'));
    },
  });
}

export function useRestoreCard(
  boardId: string,
): UseMutationResult<CardDetail, unknown, RestoreCardVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables: RestoreCardVariables) =>
      restoreCard(
        variables.cardId,
        variables.listId === undefined ? undefined : { listId: variables.listId },
      ),
    onSuccess: (detail, variables) => {
      queryClient.setQueryData(queryKeys.cards.detail(variables.cardId), detail);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.archiveAll(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(boardId),
      });
    },
    onError: () => {
      announce(t('primitives.cardHooks.restoreFailed'));
    },
  });
}

export function useDeleteCard(
  boardId: string,
): UseMutationResult<null, unknown, DeleteCardVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables: DeleteCardVariables) => deleteCard(variables.cardId),
    onSuccess: (_deleted, variables) => {
      queryClient.removeQueries({ queryKey: queryKeys.cards.detail(variables.cardId) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.archiveAll(boardId),
      });
    },
    onError: () => {
      announce(t('primitives.cardHooks.deleteFailed'));
    },
  });
}

export function useUpdateCardDescription(): UseMutationResult<
  CardDetail,
  unknown,
  UpdateCardDescriptionVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables) =>
      patchCardDescription(
        variables.cardId,
        {
          description: variables.description,
        },
        { fieldVersion: variables.fieldVersion },
      ),
    onSuccess: (detail, variables) => {
      queryClient.setQueryData(queryKeys.cards.detail(variables.cardId), detail);
      setBoardContentsQueries(queryClient, variables.boardId, (previous) =>
        applyCardDetailToBoardContents(previous, detail),
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.fieldVersions(variables.cardId),
      });
    },
    onError: () => {
      announce(t('primitives.cardHooks.descriptionFailed'));
    },
  });
}

function applyCardDetailSuccess(
  queryClient: ReturnType<typeof useQueryClient>,
  detail: CardDetail,
  variables: { readonly cardId: string; readonly boardId: string },
): void {
  queryClient.setQueryData(queryKeys.cards.detail(variables.cardId), detail);
  setBoardContentsQueries(queryClient, variables.boardId, (previous) =>
    applyCardDetailToBoardContents(previous, detail),
  );
}

export function useUpdateCardStartDate(): UseMutationResult<
  CardDetail,
  unknown,
  UpdateCardStartDateVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables) =>
      patchCardStartDate(variables.cardId, { startAt: variables.startAt }),
    onSuccess: (detail, variables) => {
      applyCardDetailSuccess(queryClient, detail, variables);
    },
    onError: () => {
      announce(t('primitives.cardHooks.startDateFailed'));
    },
  });
}

export function useUpdateCardDueDate(): UseMutationResult<
  CardDetail,
  unknown,
  UpdateCardDueDateVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables) =>
      patchCardDueDate(variables.cardId, {
        dueAt: variables.dueAt,
        ...(variables.dueAllDay === undefined ? {} : { dueAllDay: variables.dueAllDay }),
      }),
    onSuccess: (detail, variables) => {
      applyCardDetailSuccess(queryClient, detail, variables);
    },
    onError: () => {
      announce(t('primitives.cardHooks.dueDateFailed'));
    },
  });
}

export function useUpdateCardDueReminder(): UseMutationResult<
  CardDetail,
  unknown,
  UpdateCardDueReminderVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables) =>
      patchCardDueReminder(variables.cardId, {
        dueReminderMinutesBefore: variables.dueReminderMinutesBefore,
      }),
    onSuccess: (detail, variables) => {
      applyCardDetailSuccess(queryClient, detail, variables);
    },
    onError: () => {
      announce(t('primitives.cardHooks.dueReminderFailed'));
    },
  });
}

export function useSetCardCompleted(): UseMutationResult<
  CardDetail,
  unknown,
  SetCardCompletedVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables) =>
      patchCardCompleted(variables.cardId, { completed: variables.completed }),
    onSuccess: (detail, variables) => {
      applyCardDetailSuccess(queryClient, detail, variables);
      void queryClient.invalidateQueries({ queryKey: queryKeys.meHome() });
    },
    onError: () => {
      announce(t('primitives.cardHooks.completionFailed'));
    },
  });
}

export function useSetCardCover(): UseMutationResult<
  CardDetail,
  unknown,
  SetCardCoverVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables) => putCardCover(variables.cardId, variables.cover),
    onSuccess: (detail, variables) => {
      applyCardDetailSuccess(queryClient, detail, variables);
    },
    onError: () => {
      announce(t('primitives.cardHooks.coverUpdateFailed'));
    },
  });
}

export function useRemoveCardCover(): UseMutationResult<
  CardDetail,
  unknown,
  RemoveCardCoverVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables) => deleteCardCover(variables.cardId),
    onSuccess: (detail, variables) => {
      applyCardDetailSuccess(queryClient, detail, variables);
    },
    onError: () => {
      announce(t('primitives.cardHooks.coverRemoveFailed'));
    },
  });
}

export function useAddCardMember(): UseMutationResult<
  CardDetail,
  unknown,
  ToggleCardMemberVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables) =>
      addCardMember(variables.cardId, { userId: variables.userId }),
    onSuccess: (detail, variables) => {
      queryClient.setQueryData(queryKeys.cards.detail(variables.cardId), detail);
      queryClient.setQueryData(queryKeys.boards.contents(variables.boardId), (previous) =>
        applyCardDetailToBoardContents(previous as BoardContents | undefined, detail),
      );
    },
    onError: () => {
      announce(t('primitives.cardHooks.membersFailed'));
    },
  });
}

export function useRemoveCardMember(): UseMutationResult<
  CardDetail,
  unknown,
  ToggleCardMemberVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables) => removeCardMember(variables.cardId, variables.userId),
    onSuccess: (detail, variables) => {
      queryClient.setQueryData(queryKeys.cards.detail(variables.cardId), detail);
      queryClient.setQueryData(queryKeys.boards.contents(variables.boardId), (previous) =>
        applyCardDetailToBoardContents(previous as BoardContents | undefined, detail),
      );
    },
    onError: () => {
      announce(t('primitives.cardHooks.membersFailed'));
    },
  });
}
